// theme-context.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { APP_CONFIG } from "../../constants/constants";
import { API_ENDPOINTS, API_SERVER_URL } from "../../api/network/endpoints";
import { WhiteLabelConfig } from "../../api/types/WhiteLabelConfig";

type ThemeColors = {
  dark: string;
  bright: string;
  pale: string;
  fade: string;
};

type ThemeAssets = {
  logoUrl: string;
  faviconUrl: string;
  landingScreenImageUrl: string;
  landing_page_heading?: string;
  landing_page_content?: string;
};

type ThemeContextType = {
  colors: ThemeColors;
  setColors: (colors: Partial<ThemeColors>) => void;
  assets: ThemeAssets;
};

const defaultColors: ThemeColors = {
  dark: "#005B90",
  bright: "#0E88FF",
  pale: "#C2DCF2",
  fade: "#C2DCF266",
};

// const defaultColors: ThemeColors = {
//   dark: "#30746C",
//   bright: "#3F978C",
//   pale: "#52CBBD",
//   fade: "#BCF1EB",
// };

const ThemeContext = createContext<ThemeContextType>({
  colors: defaultColors,
  setColors: () => {},
  assets: {
    logoUrl: "",
    faviconUrl: "",
    landingScreenImageUrl: "",
    landing_page_heading: "",
    landing_page_content: "",
  },
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [colors, setColorsState] = useState<ThemeColors>(defaultColors);

  const [assets, setAssets] = useState<ThemeAssets>({
    logoUrl: "",
    faviconUrl: "",
    landingScreenImageUrl: "",
  });

  // Get from URL
  useEffect(() => {
    // Get domain name
    const url = window.location.origin;

    const BASE_SERVER_URL = API_SERVER_URL;
    // Fetch colors config from API based on domain
    fetch(
      `${BASE_SERVER_URL}${API_ENDPOINTS.ADMINS.WHITE_LABEL_CONFIG}?domain=${url}`
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          const dataConfig: WhiteLabelConfig = data?.results?.[0] ?? null;
          if (APP_CONFIG.DEBUG.ALL) console.log("dataConfig", dataConfig);
          if (dataConfig) {
            setColors({
              dark: dataConfig?.color_dark,
              bright: dataConfig?.color_bright,
              pale: dataConfig?.color_pale,
              fade: dataConfig?.color_faded,
            });
            setAssets({
              faviconUrl: dataConfig?.favicon as string,
              logoUrl: dataConfig?.login_screen_logo as string,
              landingScreenImageUrl: dataConfig?.landing_page_image as string,
              landing_page_heading: dataConfig?.landing_page_heading,
              landing_page_content: dataConfig?.landing_page_content,
            });
            // Set CSS variables
            document.documentElement.style.setProperty("--dark", colors.dark);
            document.documentElement.style.setProperty(
              "--bright",
              colors.bright
            );
            document.documentElement.style.setProperty("--pale", colors.pale);
            document.documentElement.style.setProperty("--fade", colors.fade);
            // Set title of the page
            document.title = dataConfig?.brand_name || APP_CONFIG.APP_NAME;
            // Set meta tags
            setMetaTag("title", dataConfig?.meta_title || APP_CONFIG.APP_NAME);
            setMetaTag(
              "description",
              dataConfig?.meta_description || APP_CONFIG.APP_NAME
            );
          }
        }
      })
      .catch((error) => {
        if (APP_CONFIG.DEBUG.ALL)
          console.log("Unable to fetch color config", error);
      });
  }, []);

  // Update CSS variables
  useEffect(() => {
    document.documentElement.style.setProperty("--dark", colors.dark);
    document.documentElement.style.setProperty("--bright", colors.bright);
    document.documentElement.style.setProperty("--pale", colors.pale);
    document.documentElement.style.setProperty("--fade", colors.fade);
  }, [colors]);

  // Set favicon and Title if available
  useEffect(() => {
    if (assets?.faviconUrl) {
      if (APP_CONFIG.DEBUG.ALL)
        console.log("Setting favicon to:", assets.faviconUrl);
      changeFavicon(assets.faviconUrl);
    }
  }, [assets]);

  const changeFavicon = (url: string) => {
    let link =
      (document.querySelector("link[rel~='icon']") as HTMLLinkElement | null) ||
      (document.createElement("link") as HTMLLinkElement);
    if (!link) {
      link = document.createElement("link") as HTMLLinkElement;
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    link.href = url;
  };

  const setColors = (newColors: Partial<ThemeColors>) => {
    setColorsState((prev) => ({ ...prev, ...newColors }));
  };

  const setMetaTag = (metaTitle: string, metaDesc: string) => {
    let element = document.querySelector(
      `meta[name='${metaTitle}']`
    ) as HTMLMetaElement | null;

    if (!element) {
      element = document.createElement("meta");
      element.name = metaTitle;
      document.head.appendChild(element);
    }

    element.content = metaDesc;
  };

  return (
    <ThemeContext.Provider value={{ assets, colors, setColors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeAssets = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeAssets must be used within a ThemeProvider");
  }
  return context;
};

export const useTheme = () => useContext(ThemeContext);
